import { useEffect, useMemo, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Wine, Activity, ShoppingBag, Trash2, Check, BarChart3, Package, TrendingUp, AlertTriangle, Tags, Download } from "lucide-react";
import {
  getProducts, createProduct, updateProduct, deleteProduct, getProductsSalesSummary, downloadCsv, fmt,
} from "@/lib/api";

const UNIT_TYPE_LABELS = {
  private: "Private Room",
  apartment: "Apartment",
  shared_bed: "Shared Bed",
  private_shared_bath: "Private · shared bath",
};
const UNIT_TYPES = ["private", "apartment", "shared_bed", "private_shared_bath"];

const CAT_META = {
  bar: { label: "Bar", icon: Wine, color: "text-amber-600 bg-amber-50" },
  activities: { label: "Activities", icon: Activity, color: "text-emerald-600 bg-emerald-50" },
  shop: { label: "Shop", icon: ShoppingBag, color: "text-blue-600 bg-blue-50" },
};
const CATS = ["bar", "activities", "shop"];

export default function Products() {
  const [tab, setTab] = useState("bar");
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState({ products: [], totals: {}, best_seller: null });
  const [addOpen, setAddOpen] = useState(false);
  const [newP, setNewP] = useState({ name: "", category: "bar", price: "", stock: "" });
  const [editRowId, setEditRowId] = useState(null);
  const [editVals, setEditVals] = useState({ name: "", price: "", stock: "" });
  const [invCatFilter, setInvCatFilter] = useState("all");
  const [variantsProduct, setVariantsProduct] = useState(null);
  const [variantVals, setVariantVals] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    const [ps, s] = await Promise.all([getProducts(), getProductsSalesSummary()]);
    setProducts(ps);
    setSales(s);
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!newP.name || !newP.price) return toast.error("Name and price required");
    try {
      await createProduct({
        name: newP.name,
        category: newP.category,
        price: parseFloat(newP.price),
        stock: parseInt(newP.stock) || 0,
      });
      toast.success("Product added");
      setAddOpen(false);
      setNewP({ name: "", category: tab, price: "", stock: "" });
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to add product";
      toast.error(typeof msg === "string" ? msg : "Failed to add product");
    }
  };

  const startEdit = (p) => {
    setEditRowId(p.id);
    setEditVals({ name: p.name, price: String(p.price), stock: String(p.stock ?? 0) });
  };

  const saveEdit = async (id, includeStock) => {
    try {
      const updates = { name: editVals.name, price: parseFloat(editVals.price) };
      if (includeStock) updates.stock = parseInt(editVals.stock) || 0;
      await updateProduct(id, updates);
      toast.success("Updated");
      setEditRowId(null);
      load();
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteProduct(id);
      toast.success("Product deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to delete product";
      toast.error(typeof msg === "string" ? msg : "Failed to delete product");
    }
  };

  const openVariants = (p) => {
    setVariantsProduct(p);
    const starting = {};
    for (const ut of UNIT_TYPES) {
      starting[ut] = (p.variant_prices && p.variant_prices[ut] != null) ? String(p.variant_prices[ut]) : "";
    }
    setVariantVals(starting);
  };

  const saveVariants = async () => {
    if (!variantsProduct) return;
    const clean = {};
    for (const ut of UNIT_TYPES) {
      const v = parseFloat(variantVals[ut]);
      if (!Number.isNaN(v) && v > 0) clean[ut] = v;
    }
    try {
      await updateProduct(variantsProduct.id, { variant_prices: clean });
      toast.success("Variant prices saved");
      setVariantsProduct(null);
      load();
    } catch {
      toast.error("Failed to save variants");
    }
  };

  // Derived: sales rows filtered by inventory-tab filter
  const salesRows = useMemo(() => {
    const rows = sales.products || [];
    return invCatFilter === "all" ? rows : rows.filter(r => r.category === invCatFilter);
  }, [sales, invCatFilter]);

  const shopLowStock = (sales.products || []).filter(p => p.category === "shop" && p.stock <= 3);

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Products</h1>
          <p className="text-sm text-slate-500 mt-1">Manage items, stock and per-product sales</p>
        </div>
        <Button
          onClick={() => { setNewP({ name: "", category: tab === "inventory" ? "shop" : tab, price: "", stock: "" }); setAddOpen(true); }}
          className="bg-blue-600 hover:bg-blue-700 text-white h-11"
          data-testid="products-add-btn"
        >
          + Add Product
        </Button>
      </div>

      <Card className="shadow-none border-slate-200 overflow-hidden">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4 w-full rounded-none border-b border-slate-200 bg-white h-12">
            {CATS.map(cat => {
              const m = CAT_META[cat];
              const Icon = m.icon;
              return (
                <TabsTrigger
                  key={cat}
                  value={cat}
                  className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 rounded-none h-full"
                  data-testid={`products-tab-${cat}`}
                >
                  <Icon size={14} className="mr-2" strokeWidth={1.5} /> {m.label}
                </TabsTrigger>
              );
            })}
            <TabsTrigger
              value="inventory"
              className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 rounded-none h-full"
              data-testid="products-tab-inventory"
            >
              <BarChart3 size={14} className="mr-2" strokeWidth={1.5} /> Inventory & Sales
            </TabsTrigger>
          </TabsList>

          {CATS.map(cat => {
            const list = products.filter(p => p.category === cat);
            const isShop = cat === "shop";
            return (
              <TabsContent key={cat} value={cat} className="mt-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Name</TableHead>
                      <TableHead className="text-right w-32">Price</TableHead>
                      {isShop && <TableHead className="text-right w-28">Stock</TableHead>}
                      <TableHead className="w-28 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.map(p => (
                      <TableRow key={p.id} className="border-slate-100" data-testid={`product-row-${p.id}`}>
                        <TableCell>
                          {editRowId === p.id ? (
                            <Input value={editVals.name} onChange={(e) => setEditVals(v => ({ ...v, name: e.target.value }))} className="h-8" />
                          ) : (
                            <button onClick={() => startEdit(p)} className="font-medium text-slate-900 hover:text-blue-600 text-left">
                              {p.name}
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {editRowId === p.id ? (
                            <Input type="number" step="0.01" value={editVals.price} onChange={(e) => setEditVals(v => ({ ...v, price: e.target.value }))} className="h-8 w-24 ml-auto text-right" />
                          ) : (
                            <div className="flex items-center justify-end gap-2">
                              {p.variant_prices && Object.keys(p.variant_prices).length > 0 ? (
                                <button
                                  onClick={() => openVariants(p)}
                                  className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100"
                                  data-testid={`product-variants-${p.id}`}
                                  title="Edit per-room-type price"
                                >
                                  <Tags size={11} /> {Object.keys(p.variant_prices).length} variants
                                </button>
                              ) : null}
                              <button onClick={() => startEdit(p)} className="font-medium text-slate-900 hover:text-blue-600">
                                {fmt(p.price)}
                              </button>
                            </div>
                          )}
                        </TableCell>
                        {isShop && (
                          <TableCell className="text-right">
                            {editRowId === p.id ? (
                              <Input type="number" value={editVals.stock} onChange={(e) => setEditVals(v => ({ ...v, stock: e.target.value }))} className="h-8 w-20 ml-auto text-right" data-testid={`product-stock-input-${p.id}`} />
                            ) : (
                              <button
                                onClick={() => startEdit(p)}
                                className={`font-semibold hover:text-blue-600 ${p.stock <= 0 ? "text-red-600" : p.stock <= 3 ? "text-amber-600" : "text-slate-900"}`}
                                data-testid={`product-stock-${p.id}`}
                              >
                                {p.stock ?? 0}
                              </button>
                            )}
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          {editRowId === p.id ? (
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700" onClick={() => saveEdit(p.id, isShop)} data-testid={`product-save-${p.id}`}>
                                <Check size={14} />
                              </Button>
                              <Button size="sm" variant="outline" className="h-8" onClick={() => setEditRowId(null)}>Cancel</Button>
                            </div>
                          ) : (
                            <button onClick={() => setConfirmDelete(p)} className="text-slate-300 hover:text-red-500 p-1" data-testid={`product-delete-${p.id}`}>
                              <Trash2 size={14} />
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {list.length === 0 && (
                      <TableRow><TableCell colSpan={isShop ? 4 : 3} className="text-center py-12 text-slate-400 text-sm">No products in this category</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
                {cat === "activities" && (
                  <div className="px-5 py-3 text-xs text-slate-500 border-t border-slate-100 bg-slate-50/60">
                    Tip: products with per-room-type pricing (e.g. "Extra Night") show a <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold"><Tags size={10} /> variants</span> chip. Click it to adjust prices per room type.
                  </div>
                )}
              </TabsContent>
            );
          })}

          {/* Inventory & Sales tab */}
          <TabsContent value="inventory" className="mt-0 p-0" data-testid="inventory-tab-content">
            {/* KPI strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-5 border-b border-slate-200">
              <div className="rounded-md border border-slate-200 p-4">
                <div className="text-xs uppercase tracking-wider text-slate-500">Best seller</div>
                {sales.best_seller ? (
                  <>
                    <div className="font-heading text-xl font-bold text-slate-900 mt-2 truncate" data-testid="best-seller-name">{sales.best_seller.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5 capitalize">{sales.best_seller.category} · {sales.best_seller.units_sold} sold · {fmt(sales.best_seller.revenue)}</div>
                  </>
                ) : <div className="text-sm text-slate-400 mt-2">No sales yet</div>}
              </div>
              <div className="rounded-md border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Total units sold</div>
                  <Package size={16} className="text-blue-600" strokeWidth={1.75} />
                </div>
                <div className="font-heading text-2xl font-bold text-slate-900 mt-2" data-testid="total-units-sold">{sales.totals?.units_sold || 0}</div>
                <div className="text-xs text-slate-500 mt-0.5">{sales.totals?.products_tracked || 0} products tracked</div>
              </div>
              <div className="rounded-md border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Total sales revenue</div>
                  <TrendingUp size={16} className="text-emerald-600" strokeWidth={1.75} />
                </div>
                <div className="font-heading text-2xl font-bold text-slate-900 mt-2" data-testid="total-sales-revenue">{fmt(sales.totals?.revenue || 0)}</div>
                <div className="text-xs text-slate-500 mt-0.5">All time · all categories</div>
              </div>
              <div className="rounded-md border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Shop stock on hand</div>
                  <ShoppingBag size={16} className="text-blue-600" strokeWidth={1.75} />
                </div>
                <div className="font-heading text-2xl font-bold text-slate-900 mt-2" data-testid="stock-on-hand">{sales.totals?.stock_on_hand || 0}</div>
                {shopLowStock.length > 0 ? (
                  <div className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
                    <AlertTriangle size={11} /> {shopLowStock.length} item{shopLowStock.length === 1 ? "" : "s"} low
                  </div>
                ) : <div className="text-xs text-slate-500 mt-0.5">All stocked up</div>}
              </div>
            </div>

            {/* Filter */}
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
              <div className="text-xs uppercase tracking-wider text-slate-500">Filter:</div>
              <Select value={invCatFilter} onValueChange={setInvCatFilter}>
                <SelectTrigger className="w-[160px] h-9" data-testid="inventory-category-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  <SelectItem value="bar">Bar</SelectItem>
                  <SelectItem value="activities">Activities</SelectItem>
                  <SelectItem value="shop">Shop</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-500 ml-auto">Sorted by revenue (high → low)</div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadCsv(
                  "/products/sales-summary.csv",
                  invCatFilter && invCatFilter !== "all" ? { category: invCatFilter } : {},
                  `inventory_sales${invCatFilter && invCatFilter !== "all" ? `_${invCatFilter}` : ""}_${new Date().toISOString().slice(0, 10)}.csv`,
                )}
                className="h-9"
                data-testid="inventory-csv-btn"
              >
                <Download size={14} /> Export CSV
              </Button>
            </div>

            {/* Per-product table */}
            <Table data-testid="inventory-sales-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-10 text-center">#</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-center">Stock</TableHead>
                  <TableHead className="text-center">Units sold</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salesRows.map((r, idx) => {
                  const m = CAT_META[r.category];
                  const Icon = m?.icon;
                  const isShop = r.category === "shop";
                  const lowStock = isShop && r.stock <= 3 && r.stock > 0;
                  const outOfStock = isShop && r.stock <= 0;
                  return (
                    <TableRow key={r.product_id} className="border-slate-100" data-testid={`inventory-row-${r.product_id}`}>
                      <TableCell className="text-center text-xs text-slate-400 font-semibold">{idx + 1}</TableCell>
                      <TableCell className="font-medium text-slate-900">{r.name}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs ${m?.color || ""} capitalize`}>
                          {Icon && <Icon size={11} strokeWidth={1.75} />} {m?.label || r.category}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-slate-700">{fmt(r.price)}</TableCell>
                      <TableCell className="text-center">
                        {isShop ? (
                          <span className={`font-semibold ${outOfStock ? "text-red-600" : lowStock ? "text-amber-600" : "text-slate-900"}`}>
                            {r.stock}{outOfStock && <span className="ml-1 text-[10px] uppercase">out</span>}{lowStock && <span className="ml-1 text-[10px] uppercase">low</span>}
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="inline-flex items-center justify-center h-6 min-w-[28px] px-2 rounded bg-blue-100 text-blue-700 text-xs font-semibold">{r.units_sold}</span>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-slate-900">{fmt(r.revenue)}</TableCell>
                    </TableRow>
                  );
                })}
                {salesRows.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400 text-sm">No products</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TabsContent>
        </Tabs>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="add-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Product</DialogTitle>
            <DialogDescription className="sr-only">Add a new product to Bar, Activities or Shop.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label>
              <Input value={newP.name} onChange={(e) => setNewP(p => ({ ...p, name: e.target.value }))} className="mt-1 h-11" autoFocus data-testid="new-product-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Category</Label>
              <Select value={newP.category} onValueChange={(v) => setNewP(p => ({ ...p, category: v }))}>
                <SelectTrigger className="mt-1 h-11" data-testid="new-product-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">Bar</SelectItem>
                  <SelectItem value="activities">Activities</SelectItem>
                  <SelectItem value="shop">Shop</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Price (€)</Label>
                <Input type="number" step="0.01" value={newP.price} onChange={(e) => setNewP(p => ({ ...p, price: e.target.value }))} className="mt-1 h-11" data-testid="new-product-price" />
              </div>
              {newP.category === "shop" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Initial Stock</Label>
                  <Input type="number" value={newP.stock} onChange={(e) => setNewP(p => ({ ...p, stock: e.target.value }))} className="mt-1 h-11" placeholder="0" data-testid="new-product-stock" />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-product-save">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Variant price editor */}
      <Dialog open={!!variantsProduct} onOpenChange={(o) => { if (!o) setVariantsProduct(null); }}>
        <DialogContent className="max-w-md" data-testid="variants-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Variant pricing · {variantsProduct?.name}</DialogTitle>
            <DialogDescription className="text-xs">
              Set a price per room type. When this item is charged, the system automatically uses the price for the guest's room. Leave a field blank to fall back to the default price ({fmt(variantsProduct?.price || 0)}).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {UNIT_TYPES.map(ut => (
              <div key={ut} className="flex items-center justify-between gap-3">
                <Label className="text-sm text-slate-700 flex-1">{UNIT_TYPE_LABELS[ut]}</Label>
                <div className="relative w-36">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={variantVals[ut] || ""}
                    onChange={(e) => setVariantVals(v => ({ ...v, [ut]: e.target.value }))}
                    className="h-10 pl-7 text-right font-semibold"
                    placeholder="—"
                    data-testid={`variant-price-${ut}`}
                  />
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setVariantsProduct(null)}>Cancel</Button>
            <Button onClick={saveVariants} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="variants-save">
              Save variant prices
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="product-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This product will no longer be available in the POS or consumption dialogs.
              Existing sales records already recorded on past reservations are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && handleDelete(confirmDelete.id)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="product-delete-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
